import { useMemo, useState } from "react";
import ReportActions from "./ReportActions";
import ReportFiltersPanel from "./ReportFiltersPanel";
import ReportPreview from "./ReportPreview";
import ReportTypePicker from "./ReportTypePicker";
import { DEFAULT_STATUT_CONFIG, type ReportFilters, type ReportsWorkspaceProps } from "./types";
import { appliedFilterLines, EMPTY_FILTERS, reportFilename } from "./utils";

export default function ReportsWorkspace({
  heading,
  intro,
  reportTypes,
  options,
  getStatutConfig,
  buildReport,
  filenamePrefix,
}: ReportsWorkspaceProps) {
  const [selectedType, setSelectedType] = useState(reportTypes[0]?.id ?? "");
  const [filters, setFilters] = useState<ReportFilters>(EMPTY_FILTERS);
  const [generated, setGenerated] = useState<ReturnType<ReportsWorkspaceProps["buildReport"]> | null>(null);
  const [snapshot, setSnapshot] = useState<ReportFilters>(EMPTY_FILTERS);

  const statutConfig = getStatutConfig ? getStatutConfig(selectedType) : DEFAULT_STATUT_CONFIG;
  const showProprietaire = Boolean(options.proprietaires);

  const immeubleOptions = useMemo(
    () => [
      { value: "", label: "Tous les immeubles" },
      ...options.immeubles
        .filter((item) => !filters.proprietaireId || item.proprietaireId === filters.proprietaireId)
        .map((item) => ({ value: item.value, label: item.label })),
    ],
    [options.immeubles, filters.proprietaireId]
  );

  const bienOptions = useMemo(
    () => [
      { value: "", label: "Tous les biens" },
      ...options.biens
        .filter((item) => !filters.immeubleId || item.immeubleId === filters.immeubleId)
        .filter((item) => !filters.proprietaireId || item.proprietaireId === filters.proprietaireId)
        .map((item) => ({ value: item.value, label: item.label })),
    ],
    [options.biens, filters.immeubleId, filters.proprietaireId]
  );

  const locataireOptions = useMemo(
    () => [
      { value: "", label: "Tous les locataires" },
      ...options.locataires
        .filter((item) => !filters.proprietaireId || item.proprietaireId === filters.proprietaireId)
        .map((item) => ({ value: item.value, label: item.label })),
    ],
    [options.locataires, filters.proprietaireId]
  );

  const proprietaireOptions = useMemo(
    () => [{ value: "", label: "Tous les propriétaires" }, ...(options.proprietaires ?? [])],
    [options.proprietaires]
  );

  const statutOptions = useMemo(
    () => [{ value: "", label: "Tous" }, ...statutConfig.options],
    [statutConfig.options]
  );

  const isFiltering =
    Boolean(filters.proprietaireId) ||
    Boolean(filters.immeubleId) ||
    Boolean(filters.bienId) ||
    Boolean(filters.locataireId) ||
    Boolean(filters.statut) ||
    Boolean(filters.dateFrom) ||
    Boolean(filters.dateTo);

  const handleTypeChange = (id: string) => {
    setSelectedType(id);
    setFilters((prev) => ({ ...prev, statut: "" }));
    setGenerated(null);
  };

  const handleFilterChange = (patch: Partial<ReportFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setGenerated(null);
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    setGenerated(null);
  };

  const handleGenerate = () => {
    setGenerated(buildReport(selectedType, filters));
    setSnapshot(filters);
  };

  const filterLines = generated ? appliedFilterLines(snapshot, options, statutConfig) : [];

  return (
    <div className="flex flex-col gap-6">
      <div className="max-w-3xl">
        <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">{heading}</h2>
        <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{intro}</p>
      </div>

      <ReportTypePicker reportTypes={reportTypes} value={selectedType} onChange={handleTypeChange} />

      <ReportFiltersPanel
        filters={filters}
        onChange={handleFilterChange}
        onReset={handleReset}
        showProprietaire={showProprietaire}
        immeubleOptions={immeubleOptions}
        bienOptions={bienOptions}
        locataireOptions={locataireOptions}
        proprietaireOptions={proprietaireOptions}
        statutConfig={{ label: statutConfig.label, options: statutOptions }}
        isFiltering={isFiltering}
      />

      <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between md:p-5">
        <div>
          <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
            3. Générez l'aperçu du rapport
          </h3>
          <p className="mt-0.5 text-xs text-foreground-500">
            L'aperçu détaille les totaux et chaque ligne avant tout téléchargement.
          </p>
        </div>
        <button
          type="button"
          onClick={handleGenerate}
          className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-file-chart-line text-base" aria-hidden="true" />
          Générer le rapport
        </button>
      </div>

      {generated && (
        <>
          <ReportPreview result={generated} filterLines={filterLines} />
          <ReportActions
            result={generated}
            filterLines={filterLines}
            filename={reportFilename(filenamePrefix, selectedType)}
          />
        </>
      )}
    </div>
  );
}